/**
 * UserLogProvider — wires the singleton UserLogClient into React.
 *
 * Responsibilities:
 *   - Call userLogClient.start() once at mount with the backend endpoint.
 *   - Observe AuthContext and forward identity changes to the client so
 *     emitted events carry the current user_id / roles / email.
 *   - Clean up on unmount (mostly for HMR / test safety).
 *
 * Place INSIDE <AuthProvider> and inside <BrowserRouter>. No context value
 * exposed: consumers use useUserLog() which reads the singleton directly.
 */

import React, { ReactNode, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { userLogClient } from "./userLogClient";

interface Props {
	children: ReactNode;
}

const resolveEndpoint = (): string => {
	const baseUrl = import.meta.env.VITE_BACKEND_URL;
	if (!baseUrl) return "";
	return `${String(baseUrl).replace(/\/+$/, "")}/logs/batch`;
};

export const UserLogProvider: React.FC<Props> = ({ children }) => {
	const { user, isAuthenticated } = useAuth();

	// Boot the client once per page load.
	useEffect(() => {
		const endpoint = resolveEndpoint();
		userLogClient.start({
			endpoint,
			// Disable entirely when no backend URL is configured (e.g. during
			// `vite build --mode test`). This makes logEvent a true no-op.
			enabled: !!endpoint,
		});
		return () => {
			// Flush anything still queued before tearing down (HMR / unmount).
			userLogClient.flush({ useBeacon: false });
			userLogClient.stop();
		};
	}, []);

	// Keep the singleton's identity in sync with AuthContext.
	useEffect(() => {
		if (isAuthenticated && user) {
			userLogClient.setIdentity({
				userId: user.uuid ?? null,
				userEmail: user.email ?? null,
				userRoles: Array.isArray(user.roles) ? user.roles.map(String) : null,
			});
		} else {
			userLogClient.clearIdentity();
		}
	}, [isAuthenticated, user]);

	return <>{children}</>;
};
